import type { SignInProvider } from "@neus/shared";
import appleButton from "../../assets/sign-in/apple-continue.png";
import facebookLogo from "../../assets/sign-in/facebook-white.png";
import googleLogo from "../../assets/sign-in/google.png";
import linkedInLogo from "../../assets/sign-in/linkedin.png";
import microsoftLogo from "../../assets/sign-in/microsoft.svg";
import styles from "./SignInButton.module.css";

/**
 * A provider's sign-in button, drawn the way its branding guidelines ask (README → "Sign-in providers"),
 * with the official artwork in client/src/assets/sign-in, unaltered:
 * - Google: light theme, the standard-color G at 20px, 12px before it and 10px after, Google Sans Medium 14/20.
 * - Microsoft: the light "Sign in with Microsoft" redlines: the four-square logo at 21px, Segoe UI Semibold 15px.
 * - Apple: Apple's own black "Continue with Apple" button image (appleid.cdn-apple.com, 240×41 at 2x), logo and
 *   title as Apple draws them, centered on black.
 * - Facebook: Facebook blue with the white "f" before the label.
 * - LinkedIn: the blue [in] logo on white, its preferred use.
 * All are the same size, as Google and Apple require no other sign-in option to be more prominent.
 */
export function SignInButton({ provider, href }: { provider: SignInProvider; href: string }) {
  if (provider.id === "apple") {
    return (
      <a className={`${styles.button} ${styles.apple}`} href={href}>
        <img src={appleButton} alt={provider.label} width={240} height={41} />
      </a>
    );
  }
  const logo = {
    microsoft: <img src={microsoftLogo} alt="" width={21} height={21} />,
    // The 40px square asset, cropped to its 20px G: Google's spacing around it is kept, not the art.
    google: (
      <span className={styles.googleCrop}>
        <img src={googleLogo} alt="" width={40} height={40} />
      </span>
    ),
    facebook: <img src={facebookLogo} alt="" width={20} height={20} />,
    linkedin: <img src={linkedInLogo} alt="" height={20} />,
  }[provider.id];
  return (
    <a className={`${styles.button} ${styles[provider.id]}`} href={href}>
      <span className={styles.logo}>{logo}</span>
      <span className={styles.label}>{provider.label}</span>
    </a>
  );
}
